// The workflow formats LD's registered parsers actually read (backend
// logicDecomposerService.SUPPORTED_EXTENSIONS — kept in sync manually since the frontend
// can't import backend source; a drift here reproduces exactly the item-1 bug where intake
// and parser support silently disagreed about which extensions were real).
export const LD_PARSER_EXTENSIONS = ['.xlsx', '.xlsm', '.yxmd', '.yxmc', '.dsx', '.xml', '.tflx', '.twbx'] as const;

// Formats that are UNAMBIGUOUSLY a Logic Decomposer workflow file — auto-routed out of the
// generic Migration Architect analysis box and into LD without asking. .xlsx and .xml are
// deliberately excluded: a .xlsx could be a plain data file for the generic pipeline, and
// Migration Architect's own upload also takes .xlsx generically, so forcing every .xlsx into
// LD would break that ambiguous, legitimate use. A consultant who wants a plain .xlsx workbook
// decomposed uses the LD dashboard's own upload directly, which takes it unambiguously.
export const LD_ONLY_EXTENSIONS = ['.xlsm', '.yxmd', '.yxmc', '.dsx', '.tflx', '.twbx'] as const;

export const LD_ACCEPT = LD_PARSER_EXTENSIONS.join(',');

// Formats the SOW's Day-1 dependency list names as required artifacts, but which have NO
// registered parser yet. Surfaced in the UI as "coming soon" rather than silently accepted (and
// failing three layers in) or silently omitted (which reads as "we forgot," when the honest answer
// is "no parser exists" — Asana 1216373781992284 item 1).
//
// SOW_v2 Day-1 artifacts (task 1218070214136694, subtask 3 — source-type coverage): Alteryx .yxmd,
// Access .mdb/.accdb, Excel macros, Python, R, SQL, supporting extracts. Excel formula/VBA,
// Alteryx, DataStage and Informatica already have parsers (LD_PARSER_EXTENSIONS above). Access,
// Python, R, SQL and Tableau Prep are named by the SOW but not yet parseable — they are listed
// here so intake names them honestly (a per-type "coming soon" count on the intake summary)
// instead of dropping them without a word.
export const LD_COMING_SOON_EXTENSIONS = ['.mdb', '.accdb', '.py', '.r', '.sql', '.tfl'] as const;

// Accept-list that ALSO admits the coming-soon SOW Day-1 formats, so the file picker lets a
// consultant select an Access / Python / R / SQL / Tableau-Prep file and get an honest "coming
// soon, skipped" message — rather than the OS greying it out (which reads as "unrecognized").
export const LD_ACCEPT_WITH_COMING_SOON = [...LD_PARSER_EXTENSIONS, ...LD_COMING_SOON_EXTENSIONS].join(',');

// Human labels for the intake coverage matrix — what each extension IS, so the UI can say
// "Access database (coming soon)" instead of a bare ".mdb".
export const LD_FORMAT_LABEL: Record<string, string> = {
  '.xlsx': 'Excel (formulas)', '.xlsm': 'Excel (macros / VBA)',
  '.yxmd': 'Alteryx workflow', '.yxmc': 'Alteryx macro',
  '.dsx': 'DataStage export', '.xml': 'Informatica mapping',
  '.tflx': 'Tableau Prep (packaged)', '.twbx': 'Tableau workbook',
  '.mdb': 'Access database', '.accdb': 'Access database',
  '.py': 'Python script', '.r': 'R script', '.sql': 'SQL script', '.tfl': 'Tableau Prep flow',
};

const lower = (name: string) => String(name || '').toLowerCase();
const extOf = (name: string): string => {
  const m = lower(name).match(/\.[a-z0-9]+$/);
  return m ? m[0] : '';
};

export function isLdParsable(name: string): boolean {
  return LD_PARSER_EXTENSIONS.some((ext) => lower(name).endsWith(ext));
}

export function isLdOnly(name: string): boolean {
  return LD_ONLY_EXTENSIONS.some((ext) => lower(name).endsWith(ext));
}

export function isLdComingSoon(name: string): boolean {
  return LD_COMING_SOON_EXTENSIONS.some((ext) => lower(name).endsWith(ext));
}

// The single coverage classification a file falls into, for honest intake reporting.
export type LdCoverage = 'supported' | 'coming_soon' | 'unsupported';

export function ldCoverageOf(name: string): LdCoverage {
  if (isLdParsable(name)) return 'supported';
  if (isLdComingSoon(name)) return 'coming_soon';
  return 'unsupported';
}

/** A human label for a file's format (falls back to the bare extension, then "file"). */
export function ldFormatLabel(name: string): string {
  const ext = extOf(name);
  return LD_FORMAT_LABEL[ext] || (ext ? ext.slice(1).toUpperCase() : 'file');
}

/**
 * Summarize a batch of files into supported / coming-soon / unsupported buckets with per-type
 * counts, so intake can show an honest matrix ("128 supported · 14 coming soon · 3 unsupported")
 * and never claim "154 decomposed" when 40 were skipped.
 */
export interface LdIntakeSummary {
  supported: number;
  comingSoon: number;
  unsupported: number;
  total: number;
  byType: { label: string; ext: string; coverage: LdCoverage; count: number }[];
}

export function summarizeLdIntake(names: string[]): LdIntakeSummary {
  const buckets = new Map<string, { label: string; ext: string; coverage: LdCoverage; count: number }>();
  let supported = 0, comingSoon = 0, unsupported = 0;
  for (const name of names) {
    const coverage = ldCoverageOf(name);
    if (coverage === 'supported') supported++;
    else if (coverage === 'coming_soon') comingSoon++;
    else unsupported++;
    const ext = extOf(name);
    const key = ext || '(none)';
    const existing = buckets.get(key);
    if (existing) existing.count++;
    else buckets.set(key, { label: ldFormatLabel(name), ext, coverage, count: 1 });
  }
  return {
    supported, comingSoon, unsupported, total: names.length,
    byType: Array.from(buckets.values()).sort((a, b) => b.count - a.count),
  };
}

let pending: File[] = [];

export function setPendingLdFiles(files: File[]): void {
  pending = files.filter((f) => isLdParsable(f.name));
}

export function takePendingLdFiles(): File[] {
  const out = pending;
  pending = [];
  return out;
}

export function hasPendingLdFiles(): boolean {
  return pending.length > 0;
}
